import { FC, useEffect, useMemo } from 'react';
import { Checkbox, Flex, Text, Tooltip, Box, Center, Spinner } from '@chakra-ui/react';
import { DITable } from 'plugins/DITable';
import TablePagination from 'plugins/DITable/Pagination';
import { getRetailerKeywordsObjs, getSelectedRetailers } from 'pages/Keywords/helpers/dataGenerators';
import { ChevronDownIcon, ChevronRightIcon, InfoIcon } from '@chakra-ui/icons';
import { linkStyle } from 'shared/styles';
import {
    ColumnDef,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { areChildrenChecked, checkRetailers, toggleRetailer } from 'pages/Keywords/helpers/handlers';
import { useTranslation } from 'helpers/languages/translations';
import { IUserInfo, TFormattedKeywordsByRetailer, TRetailerRow } from 'types';
import useKeywordsByRetailers from './useKeywordsByRetailers';
import RetailersModal from './KeywordsByRetailersModal';
import RetailersToolbar from './KeywordsByRetailersToolbar';

const KEYWORD_COLUMN_LIMIT = 5;

const MKeywordsViewer: FC<{ readonly: boolean; userInfo?: IUserInfo }> = ({ readonly, userInfo }) => {
    const {
        handleCloseAddAndUpdateModal,
        handleEdit,
        modalType,
        keywordFilter,
        setKeywordFilter,
        isLoading,
        updateInitialRef,
        nestedKeywordsByRetailers,
        setNestedKeywordsByRetailers,
        isUpdateRetailersLoading,
        keywordsToSelect,
        handleViewKeywords,
        setKeywordsToSelect,
        setKeywordsByType,
        filteredKeywords,
        visibleKeywords,
        handleSubmit,
        isOpen,
        treeOpenByDefault,
        setTreeOpenByDefault,
        defaultColumn,
        sortTypes,
        filters,
        setFilters,
        isExportLoading,
        isFetching,
        handleExportKeywordsByRetailer,
        languages,
    } = useKeywordsByRetailers({ readonly, userInfo });
    const selectedRetailers = getSelectedRetailers(nestedKeywordsByRetailers);
    const isToolbarDisabled = !selectedRetailers.length;


    const translate = useTranslation();

    const columns: ColumnDef<any, any>[] = useMemo(() => {

        const tableColumns: (ColumnDef<any, any> | null)[] = [
            {
                header: () => (
                    <Text as="span" display="flex" alignItems="center">
                        {translate('keywords_by_retailer.column.retailer')}
                        <Tooltip
                            label={translate('keywords_by_retailer.info.header.retailers')}
                            aria-label="Retailers tooltip"
                            placement="bottom-end"
                        >
                            <InfoIcon color="blue.500" cursor="pointer" ml="5px" />
                        </Tooltip>
                    </Text>
                ),
                accessorKey: 'name',
                sortingFn: 'caseInsensitive',
                filterFn: (row, columnId, filterValue) => {
                    const rowToFilter = row.original as TFormattedKeywordsByRetailer;
                    let retailerFound = false;
                    if (rowToFilter.name?.toLowerCase().includes((filterValue as string)?.toLowerCase())) {
                        return true;
                    }
                    if (rowToFilter.subRows) {
                        if (
                            rowToFilter?.subRows?.some((subRow) => {
                                return subRow.name?.toLowerCase().includes((filterValue as string)?.toLowerCase());
                            })
                        )
                            return true;

                    }
                    return retailerFound;
                },
                meta: { width: '50%' },
                cell: (info) => {
                    const rowValues = info.row.original as TFormattedKeywordsByRetailer | TRetailerRow;
                    const textStyles = { ml: '0px' };
                    if (rowValues.level === 'type') {
                        textStyles.ml = '0px';
                    } else if (rowValues.level === 'retailer') {
                        textStyles.ml = '20px';
                    }
                    let rowIcon: React.ReactNode = null;
                    if (rowValues.subRows?.length) {
                        const retailerParent = rowValues as TFormattedKeywordsByRetailer;
                        if (rowValues.isExpanded) {
                            rowIcon = <ChevronDownIcon onClick={() => toggleRetailer(retailerParent, setNestedKeywordsByRetailers)} />;
                        } else {
                            rowIcon = <ChevronRightIcon onClick={() => toggleRetailer(retailerParent, setNestedKeywordsByRetailers)} />;
                        }
                    }

                    const childrenChecked = rowValues.subRows && areChildrenChecked(rowValues.subRows);
                    const checkboxStyles = {
                        mr: '2',
                        ...(readonly ? { display: 'none' } : {}),
                    };

                    return (
                        <Flex alignItems={'center'}>
                            <Box {...textStyles}>
                                <span
                                    style={{
                                        marginRight: '10px',
                                        cursor: 'pointer',
                                        height: '10px',
                                        minWidth: '20px',
                                    }}
                                >
                                    {rowIcon}
                                </span>
                                <Checkbox
                                    {...checkboxStyles}
                                    onChange={() => {
                                        checkRetailers(rowValues, setNestedKeywordsByRetailers);
                                    }}
                                    isChecked={rowValues.checked}
                                    isIndeterminate={childrenChecked && !rowValues.checked}
                                ></Checkbox>
                                {rowValues.name}
                            </Box>
                        </Flex>
                    );
                },
            },
            {
                header: () => (
                    <Text as="span" display="flex" alignItems="flex-start" minH="50px">
                        {translate('keywords_by_retailer.column.keyword')}
                        <Tooltip
                            label={translate('keywords_by_retailer.info.header.keyword')}
                            aria-label="Keywords tooltip"
                        >
                            <InfoIcon color="blue.500" cursor="pointer" ml="5px" />
                        </Tooltip>
                    </Text>
                ),
                accessorKey: 'keywords',
                enableColumnFilter: false,
                enableSorting: false,
                meta: { width: '50%' },
                cell: (info) => {
                    const rowValues = info.row.original as TFormattedKeywordsByRetailer | TRetailerRow;
                    const keywordsList = getRetailerKeywordsObjs(rowValues).map((keyword) => keyword.name);

                    const cellProps = keywordsList.length
                        ? {
                            ...linkStyle,
                            textDecoration: 'none',
                            onClick: () => {
                                handleViewKeywords(getRetailerKeywordsObjs(rowValues).map((keyword) => keyword.id));
                            },
                        }
                        : {};
                    return (
                        <Box height={35}>
                            <Text {...cellProps}>
                                <>
                                    {keywordsList.length ? (
                                        <>
                                            {keywordsList
                                                .slice(0, KEYWORD_COLUMN_LIMIT)
                                                .map((keyword) => keyword)
                                                .join(', ')}
                                            {keywordsList.length > KEYWORD_COLUMN_LIMIT && (
                                                <Text as="span">
                                                    {`... (${keywordsList.length - KEYWORD_COLUMN_LIMIT} ${(
                                                        (keywordsList.length - KEYWORD_COLUMN_LIMIT > 1
                                                            ? translate('keywords_by_retailer.column.kewyords')
                                                            : translate('keywords_by_retailer.column.kewyord')) as string
                                                    ).toLocaleLowerCase()})`}
                                                </Text>
                                            )}
                                        </>
                                    ) : (
                                        <Text as="span">-</Text>
                                    )}
                                </>
                            </Text>
                        </Box>
                    );
                },
            },
        ];

        const filteredColumns = tableColumns.filter((e): e is ColumnDef<any, any> => e !== null);
        return filteredColumns;
    }, [nestedKeywordsByRetailers, userInfo, languages]);

    const table = useReactTable({
        columns,
        data: nestedKeywordsByRetailers,
        defaultColumn,
        sortingFns: sortTypes,
        initialState: { columnFilters: filters },
        getSubRows: (originalRow) => (originalRow as TFormattedKeywordsByRetailer).subRows as unknown as TFormattedKeywordsByRetailer[],
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    useEffect(() => {
        setFilters(table.getState().columnFilters as { id: string; value: string }[]);
    }, [table.getState().columnFilters]);

    return (
        <Box mt="16px">
            <RetailersToolbar
                handleEditRetailers={handleEdit}
                isDisabled={isToolbarDisabled || readonly}
                handleExportKeywordsByRetailer={handleExportKeywordsByRetailer}
                isExportLoading={isExportLoading}
                isFetching={isFetching}
            />
            {isLoading ? (
                <Center w="100%" h="200px" bg="white" border="1px solid" borderColor="gray.100">
                    <Spinner />
                </Center>
            ) : (
                <>
                    <DITable table={table} isPagination isHeaderFixed />
                    <TablePagination table={table} />
                </>
            )}

            <RetailersModal
                updateInitialRef={updateInitialRef}
                isOpen={isOpen}
                treeOpenByDefault={treeOpenByDefault}
                setTreeOpenByDefault={setTreeOpenByDefault}
                handleCloseAddAndUpdateModal={handleCloseAddAndUpdateModal}
                modalType={modalType}
                keywordFilter={keywordFilter}
                setKeywordFilter={setKeywordFilter}
                keywordsToSelect={keywordsToSelect}
                setKeywordsToSelect={setKeywordsToSelect}
                setKeywordsByType={setKeywordsByType}
                isUpdateRetailerLoading={isUpdateRetailersLoading}
                handleSubmit={handleSubmit}
                filteredKeywords={filteredKeywords}
                visibleKeywords={visibleKeywords}
                languages={languages}
            />
        </Box>
    );
};

export default MKeywordsViewer;
